import styled from 'styled-components';
import { useMemo } from 'react';
import { BUTTON_VARIANTS, GhostButton, IconButton } from 'ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { FlexSpacer } from 'ui/flexSpacer';
import { Body1, Caption, colors, H1, heading3, mediumShadow, spacing } from 'ui/theme';
import { BossBadge } from 'ui/badge/components';
import {
  ALERTS_STATES,
  GOALS_STATES,
  JUMP_BACK_IN_STATES,
  SEND_METRICS_STATES,
} from './DevPanel.constants';
import { DevPanelFeature } from './DevPanelFeature';
import { Health16 } from 'ui/icons/aurora/16px/Health16';
import { Goals16 } from 'ui/icons/aurora/16px/studio/Goals16';
import { PerformanceAlerts24 } from 'ui/icons/aurora/24px/PerformanceAlerts24';
import { CountBadge } from 'ui/menu/Styled';
import { Controlbar, ControlGroup } from 'ui/Controlbar';
import { zIndex } from 'ui/theme';
import { useDevPanelContext } from './context/DevPanelContext';
import { Logo } from './Logo';
import GreenLogo from './green-logo.png';
import GreyLogo from './grey-logo.png';
import { DevPanelFilterKeys } from './DevPanel.types';

const Container = styled.div`
  left: 30px;
  bottom: 50px;
  position: fixed;
  background-color: transparent;
  z-index: ${zIndex.popover};
`;

const ControlbarContainer = styled.div`
  width: 100%;
  background-color: transparent;
`;

const Title = styled(H1)`
  margin: 0;
  padding: 0;
  ${heading3};
`;

const StyledCaption = styled(Caption)`
  display: block;
  margin-top: ${spacing.base};
  color: ${colors.textSecondary};
`;

export const Image = styled.img`
  height: 40px;
  width: 40px;
`;

const ImageContainer = styled.div`
  position: relative;
  background-color: transparent;
  ${mediumShadow};
`;

const StyledCountBadge = styled(CountBadge)`
  top: -6px;
  right: -6px;
  position: absolute;
`;

const StyledPopoverContent = styled(PopoverContent)`
  max-height: 600px;
  width: 400px;
`;

export const DevPanel = () => {
  const {
    isDevPanelOpen,
    setIsDevPanelOpen,
    devPanelSendsState,
    setDevPanelSendsState,
    devPanelGoalsState,
    setDevPanelGoalsState,
    devPanelAlertsState,
    setDevPanelAlertsState,
    devPanelUserActivityState,
    setDevPanelUserActivityState,
    devPanelActiveFilters,
    devPanelSelectedFiltersCount,
    clearDevPanelFilters,
  } = useDevPanelContext();

  const activeFiltersText = useMemo(() => {
    return devPanelActiveFilters.map((filter) => `${filter.label}: ${filter.value}`).join(', ');
  }, [devPanelActiveFilters]);

  return (
    <Container>
      <Popover open={isDevPanelOpen} onOpenChange={setIsDevPanelOpen}>
        <StyledPopoverContent side="top">
          <FlexSpacer
            gap="plusHalf"
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <FlexSpacer justifyContent="center" alignItems="center" gap="half">
              <Logo />
              <FlexSpacer justifyContent="center" alignItems="center">
                <Title>Development Panel</Title>
                <BossBadge>Dev</BossBadge>
              </FlexSpacer>
            </FlexSpacer>

            <Body1>
              This is a development tool for testing and debugging various UI states and features on
              the homepage.
            </Body1>

            <ControlbarContainer>
              <Controlbar aria-label="Controlbar" size="small" variant="flat">
                <ControlGroup aria-label="Control group">
                  {devPanelSelectedFiltersCount > 0 && (
                    <GhostButton onClick={clearDevPanelFilters}>Clear</GhostButton>
                  )}

                  <FlexSpacer gap="base">
                    {devPanelSelectedFiltersCount > 0 && (
                      <CountBadge>{devPanelSelectedFiltersCount}</CountBadge>
                    )}
                    <Body1 style={{ color: colors.grey50 }}>
                      Current filters:{' '}
                      {devPanelSelectedFiltersCount > 0 ? activeFiltersText : ' None'}
                    </Body1>
                  </FlexSpacer>
                </ControlGroup>
              </Controlbar>
            </ControlbarContainer>

            <DevPanelFeature
              title="Jump back in"
              feature={{
                title: 'Jump back in',
                label: 'Select a state',
                name: DevPanelFilterKeys.USER_ACTIVITY,
                value: devPanelUserActivityState,
                onSelect: (feat) => setDevPanelUserActivityState(feat),
                options: JUMP_BACK_IN_STATES,
              }}
            />

            <DevPanelFeature
              title={
                <>
                  <Health16 /> Health
                </>
              }
              features={[
                {
                  title: 'Sends',
                  label: 'Select a state',
                  name: DevPanelFilterKeys.SENDS,
                  value: devPanelSendsState,
                  onSelect: (feat) => setDevPanelSendsState(feat),
                  options: SEND_METRICS_STATES,
                },
                {
                  title: (
                    <>
                      <PerformanceAlerts24 color={colors.grey60} /> Alerts
                    </>
                  ),
                  label: 'Select a state',
                  name: DevPanelFilterKeys.ALERTS,
                  value: devPanelAlertsState,
                  onSelect: (feat) => setDevPanelAlertsState(feat),
                  options: ALERTS_STATES,
                },
              ]}
            />

            <DevPanelFeature
              key={devPanelUserActivityState}
              title={
                <>
                  <Goals16 /> Goals
                </>
              }
              feature={{
                label: 'Select a state',
                name: DevPanelFilterKeys.GOALS,
                value: devPanelGoalsState,
                onSelect: (feat) => setDevPanelGoalsState(feat),
                options: GOALS_STATES,
              }}
            />
          </FlexSpacer>

          <StyledCaption>
            NOTE: The listed states are available for testing. Please reach out to Jehf or Krisha to
            request additional states.
          </StyledCaption>
        </StyledPopoverContent>
        <PopoverTrigger asChild>
          <IconButton
            variant={BUTTON_VARIANTS.GHOST}
            onClick={() => setIsDevPanelOpen((prev) => !prev)}
          >
            <ImageContainer>
              {devPanelSelectedFiltersCount ? (
                <>
                  <StyledCountBadge>{devPanelSelectedFiltersCount}</StyledCountBadge>
                  <Image src={GreenLogo} alt="Dev tool active" />
                </>
              ) : (
                <Image src={GreyLogo} alt="Dev tool inactive" />
              )}
            </ImageContainer>
          </IconButton>
        </PopoverTrigger>
      </Popover>
    </Container>
  );
};
